import { Notification, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';

const CollectionsWarningList = styled.ul`
  padding: 0 16px;

  ${Typography.S.Typography} {
    text-transform: lowercase;
  }

  ${Typography.S.Typography}:first-letter {
    text-transform: uppercase;
  }

  a {
    font-weight: 600;
    text-decoration: underline;
    text-transform: lowercase;
  }
`;

const CollectionsWarningListItem = styled.li`
  list-style-type: disc;
`;

function ValidationErrors({ pageLayoutContentRef, style }) {
  const { counter, errors } = useValidationErrorsStore();

  const validationErrors = errors || {};

  useEffect(() => {
    if (Object.keys(validationErrors)?.length > 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      pageLayoutContentRef?.current?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [validationErrors, counter]);

  return (
    <ValidationErrorsNotification
      errors={validationErrors}
      key={JSON.stringify({ counter, errors })}
      style={style}
    />
  );
}

function ValidationErrorsNotification({ errors = {}, key, style }) {
  const { t } = useTranslation();

  const isOnlyOneError =
    Object.keys(errors)?.length === 1 && errors?.[Object.keys(errors)?.[0]]?.length === 1;

  if (isOnlyOneError) {
    return (
      <Notification
        style={style}
        key={key}
        className={Object.values(errors)?.length ? 'element-vibration' : ''}
        status={Object.keys(errors)?.[0]}
        content={
          <CollectionsWarningList>
            <Typography variant="body2">
              {t('toContinuePlease')}{' '}
              <span style={{ textTransform: 'lowercase' }}>
                {errors?.[Object.keys(errors)?.[0]]?.[0]}
              </span>
            </Typography>
          </CollectionsWarningList>
        }
      />
    );
  }

  return Object.keys(errors)?.map(status => {
    const sortedErrors = errors[status]?.sort?.((a, b) => {
      const getOrderFromKey = key => {
        if (!key) return 0;

        const orderMatch = key.match(/order=(\d+)/);
        return orderMatch ? parseInt(orderMatch[1], 10) : 99;
      };

      const orderA = getOrderFromKey(a.key);
      const orderB = getOrderFromKey(b.key);

      return orderA - orderB;
    });

    return (
      <Notification
        style={style}
        status="warning"
        key={`${status}-${key}`}
        className={Object.values(errors)?.length ? 'element-vibration' : ''}
        content={
          <>
            <Typography variant="body2" emphasis="weak">
              {t('toContinuePlease')};
            </Typography>
            <CollectionsWarningList>
              {sortedErrors?.map((pageError, pageErrorIndex) => {
                return (
                  <CollectionsWarningListItem key={`page_error_${pageErrorIndex.toString()}`}>
                    <Typography variant="caption1" emphasis="weak">
                      {pageError}
                    </Typography>
                  </CollectionsWarningListItem>
                );
              })}
            </CollectionsWarningList>
          </>
        }
      />
    );
  });
}

export { ValidationErrors, ValidationErrorsNotification };

ValidationErrors.propTypes = {
  pageLayoutContentRef: PropTypes.shape({
    current: PropTypes.shape({
      scrollTo: PropTypes.func
    })
  }),
  style: PropTypes.object
};
